import {
  type PropsWithChildren,
  type ReactElement,
  Children,
  isValidElement,
  useMemo,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import * as S from './styled'

interface TabsProps<TTabs extends readonly string[]> extends PropsWithChildren {
  tabs: TTabs
  onTabClickCallback?: (id: TTabs[number]) => void
  mt?: number
  contentGap?: number
  defaultTab?: TTabs[number]
  activeTab?: TTabs[number]
}

type PaneProps<TTabId extends string> = PropsWithChildren<{ tabId: TTabId }>

const Pane = <TTabId extends string>({ children }: PaneProps<TTabId>) => {
  return <>{children}</>
}

export const Tabs = <TTabs extends readonly string[]>({
  tabs,
  onTabClickCallback,
  mt,
  contentGap,
  children,
  defaultTab = tabs[0],
  activeTab: controlledTab,
}: TabsProps<TTabs>) => {
  type TabId = TTabs[number]

  const singleTab = tabs.length === 1
  const { t } = useTranslation()
  const [uncontrolledTab, setActiveTab] = useState<TabId>(defaultTab)
  const activeTab = controlledTab ?? uncontrolledTab
  const onTabClick = (tabId: TabId) => {
    setActiveTab(tabId)
    onTabClickCallback?.(tabId)
  }
  const activeContent = useMemo(() => {
    const items = Children.toArray(children)
    const pane = items.find(
      (child): child is ReactElement<PaneProps<TabId>> =>
        isValidElement<PaneProps<TabId>>(child) &&
        child.type === Pane &&
        child.props.tabId === activeTab,
    )

    return pane ? pane.props.children : null
  }, [children, activeTab])

  return (
    <>
      {!singleTab && (
        <S.Tabs $mt={mt}>
          {tabs.map((id) => (
            <S.Tab
              key={id}
              $active={id === activeTab}
              onClick={() => onTabClick(id)}
              id={`${id}-tab`}
            >
              {t(id)}
            </S.Tab>
          ))}
        </S.Tabs>
      )}
      {children && <S.TabsContent $mt={contentGap}>{activeContent}</S.TabsContent>}
    </>
  )
}

Tabs.Pane = Pane
